import * as THREE from 'three';
import { ATLAS_SIZE, T, TILE_PX } from './textures';

/**
 * Building detail pass (docs/ASSETS.md, "Building detail textures"): four tileable surfaces baked in Blender
 * (art/models/blender/bake_tiles.py `detail-slate`, roof_detail.py the other three; packed 2 × 2 into
 * public/textures/building-detail.webp by scripts/build-building-textures.mjs) sampled in world space by the
 * shared `facade` material, so every procedural building gets them without per-building UVs:
 *
 *   0 slate      replaces the slate atlas tile on pitched roofs (courses run along the ridge)
 *   1 clay       the same on clay-tiled roofs (slate tile + the CLAY_MARK vertex tint)
 *   2 felt       replaces the felt atlas tile on flat roofs
 *   3 render     soft weathering (rain streaks, patchy paint) multiplied over every wall
 *
 * The replacement keeps the house palette: a layer is divided by its own mean colour and multiplied by the
 * mean of the atlas tile it stands in for (both read from the 1-texel mip), so only the pattern changes.
 * MEDIUM and HIGH only (a `BH_DETAIL` define); LOW and `?classicbuildings` keep the facade as it was.
 */

/** `?classicbuildings`: the building look before this pass, for A/B captures. */
export const CLASSIC_BUILDINGS = typeof location !== 'undefined' && new URLSearchParams(location.search).has('classicbuildings');

/**
 * Vertex tint that marks a slate-tiled roof quad as clay (r / b well above anything else on a slate tile).
 * The shader divides it back out, so LOW (no detail) still draws those roofs exactly as before.
 */
export const CLAY_MARK = [1, 0.82, 0.7] as const;

/** World metres per repeat of each layer. */
// (slate: 16 courses of 0.22 m, the gauge of the Blender bake in art/models/blender/bake_tiles.py)
const SCALE = [3.52, 3.0, 4.5, 3.2];
const LAYER = 512;

const BASE = import.meta.env?.BASE_URL ?? './';

/** Load the 2 × 2 sheet into a 4-layer array texture (mipmapped, repeating, sRGB). Resolves null on failure. */
export async function loadBuildingDetail(): Promise<THREE.DataArrayTexture | null> {
  try {
    const res = await fetch(`${BASE}textures/building-detail.webp`);
    if (!res.ok) return null;
    const bmp = await createImageBitmap(await res.blob());
    const c = document.createElement('canvas');
    c.width = LAYER * 2;
    c.height = LAYER * 2;
    const g = c.getContext('2d', { willReadFrequently: true })!;
    g.drawImage(bmp, 0, 0, c.width, c.height);
    bmp.close();
    const data = new Uint8Array(LAYER * LAYER * 4 * 4);
    for (let l = 0; l < 4; l++) {
      // layer l sits at column l % 2, row l / 2 of the sheet; rows are flipped so v runs up
      const img = g.getImageData((l % 2) * LAYER, Math.floor(l / 2) * LAYER, LAYER, LAYER).data;
      const base = l * LAYER * LAYER * 4;
      for (let y = 0; y < LAYER; y++) data.set(img.subarray((LAYER - 1 - y) * LAYER * 4, (LAYER - y) * LAYER * 4), base + y * LAYER * 4);
    }
    const tex = new THREE.DataArrayTexture(data, LAYER, LAYER, 4);
    tex.format = THREE.RGBAFormat;
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.magFilter = THREE.LinearFilter;
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.generateMipmaps = true;
    tex.anisotropy = 4;
    tex.needsUpdate = true;
    return tex;
  } catch {
    return null;
  }
}

const tilesPerRow = ATLAS_SIZE / TILE_PX;
const tileVec = (id: number): string => `vec2(${(id % tilesPerRow).toFixed(1)}, ${(tilesPerRow - 1 - Math.floor(id / tilesPerRow)).toFixed(1)})`;
const mark = `vec3(${CLAY_MARK.map((v) => (1 / v).toFixed(5)).join(', ')})`;

/** Vertex additions (world position and normal), inserted after `#include <worldpos_vertex>`. */
export const DETAIL_VERTEX = `
#ifdef BH_DETAIL
  vBhWPos = (modelMatrix * vec4(transformed, 1.0)).xyz;
  vBhWN = normalize(mat3(modelMatrix) * objectNormal);
#endif`;

export const DETAIL_VERTEX_HEAD = `
#ifdef BH_DETAIL
varying vec3 vBhWPos;
varying vec3 vBhWN;
#endif`;

export const DETAIL_FRAGMENT_HEAD = `
#ifdef BH_DETAIL
varying vec3 vBhWPos;
varying vec3 vBhWN;
uniform sampler2DArray uBhDetail;
uniform sampler2D uBhTileMean;
#endif`;

/** Runs right after `#include <map_fragment>` (diffuseColor = colour × atlas texel; vColor not applied yet). */
export const DETAIL_FRAGMENT = `
  {
    vec2 bhT = floor( vMapUv * ${tilesPerRow.toFixed(1)} );
    bool bhSlate = bhT == ${tileVec(T.slate)};
    bool bhClay = bhSlate && vColor.r > vColor.b * 1.25;
    // the clay mark is only a tag: take it back out of the vertex colour (applied after this)
    if ( bhClay ) diffuseColor.rgb *= ${mark};
#ifdef BH_DETAIL
    bool bhFelt = bhT == ${tileVec(T.felt)};
    vec3 bhN = normalize( vBhWN );
    float bhH = length( bhN.xz );
    vec2 bhD = bhH > 0.001 ? bhN.xz / bhH : vec2( 0.0, 1.0 );
    vec2 bhA = vec2( -bhD.y, bhD.x );
    vec2 bhUv;
    if ( bhH < 0.08 ) bhUv = vec2( vBhWPos.x, -vBhWPos.z );                                           // flat
    else if ( bhN.y > 0.25 ) bhUv = vec2( dot( vBhWPos.xz, bhA ), -dot( vBhWPos.xz, bhD ) / bhN.y ); // slope: courses along the ridge
    else bhUv = vec2( dot( vBhWPos.xz, bhA ), vBhWPos.y );                                            // wall
    float bhL = bhClay ? 1.0 : bhSlate ? 0.0 : bhFelt ? 2.0 : 3.0;
    float bhS = bhL < 0.5 ? ${SCALE[0]!.toFixed(2)} : bhL < 1.5 ? ${SCALE[1]!.toFixed(2)} : bhL < 2.5 ? ${SCALE[2]!.toFixed(2)} : ${SCALE[3]!.toFixed(2)};
    vec3 bhTex = texture( uBhDetail, vec3( bhUv / bhS, bhL ) ).rgb;
    vec3 bhMean = textureLod( uBhDetail, vec3( 0.5, 0.5, bhL ), 9.0 ).rgb + 1e-3;
    if ( bhL < 2.5 ) {
      // roofs: the painted pattern stands in for the atlas tile, at the tile's own mean colour (slate, felt)
      // or its own colour (clay)
      // (an 8 × 8 texture of the tile means, textures.ts tileMeans(): the atlas's own mip 8 read wrong from a
      // KTX2 atlas (the baked atlas, textures.ts BAKED_FACADE), and a baked tile's mean is known at build time anyway)
      vec3 bhAtlasMean = texture( uBhTileMean, ( bhT + 0.5 ) / ${tilesPerRow.toFixed(1)} ).rgb + 1e-3;
      vec3 bhPat = bhClay ? bhTex * 0.86 : bhTex / bhMean * bhAtlasMean;
      diffuseColor.rgb = diffuseColor.rgb / max( sampledDiffuseColor.rgb, vec3( 1e-3 ) ) * bhPat;
    } else if ( abs( bhN.y ) < 0.3 ) {
      // walls: weathering only, as a soft luminance modulation
      float bhK = dot( bhTex, vec3( 0.3, 0.55, 0.15 ) ) / dot( bhMean, vec3( 0.3, 0.55, 0.15 ) );
      diffuseColor.rgb *= mix( 1.0, clamp( bhK, 0.6, 1.3 ), 0.7 );
    }
#endif
  }`;
